import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { safe_get } from "../../../report-visuals/report-utils";
import { GeopolDocument } from "../types";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { classNames } from "../../../helpers";
import { LoadingState } from "../../../components";
import { toast } from "react-toastify";

interface DemographicProfileFormProps {
  document: GeopolDocument | null;
  documentId: string | null;
  onSave: (updates: any, generatedProfile: any) => void;
  onCancel: () => void;
  rootStore: any;
}

const GENDER_OPTIONS = [
  "Male",
  "Female",
  "Prefer not to say"
];

const AGE_RANGE_OPTIONS = [
  "20-24",
  "25-35",
  "36-45",
  "46-55",
  "56-65",
  "65+"
];

const PROFESSION_OPTIONS = [
  "Academia: Student",
  "Academia: Educator",
  "Business Leaders",
  "Think Tanks",
  "Media/Journalism",
  "Civil Services",
  "Gov Emp",
  "General Public",
  "Military/Defense",
  "Others"
];

const CITY_OPTIONS = [
  "Indore",
  "Nagpur",
  "Bhopal",
  "Kolkata",
  "Jamshedpur",
  "Patna",
  "Delhi",
  "Sonipat",
  "Jaipur",
  "Lucknow",
  "Guwahati",
  "Shillong",
  "Bangalore",
  "Chennai",
  "Hyderabad",
  "Vishakapatanam",
  "Kozhikode",
  "Kochi",
  "Salem",
  "Coimbatore",
  "Thiruvananthapuram",
  "Mumbai",
  "Ahmedabad",
  "Surat",
  "Pune"
];

const LEANING_OPTIONS = [
  "Right",
  "Center Right",
  "Center",
  "Center Left",
  "Left",
  "Non Partisan",
  "Unknown"
];

const EXPERIENCE_OPTIONS = [
  "0-5 years",
  "6-10 years",
  "11-20 years",
  "21-30 years",
  "30+ years"
];

export const DemographicProfileForm: React.FC<DemographicProfileFormProps> = observer(
  ({ document, documentId, onSave, onCancel, rootStore }) => {
    const [formData, setFormData] = useState({
      gender_gender_select: "",
      age_range_age_select: "",
      professional_background_background_select: "",
      professional_experience_experience_select: "",
      metro_city_city_select: "",
      political_preference_preference_select: "",
    });

    const [abstractProfileData, setAbstractProfileData] = useState({
      additionalContext: "",
      respondentCode: "",
      respondentBio: "",
    });

    const [generating, setGenerating] = useState(false);

    useEffect(() => {
      if (document) {
        const store = rootStore?.geopolTransformerStore;
        const sourceProfile = document.demographic_profile_of_respondents || {};
        
        // Check if row transformer exists and has demographic_override
        let profileToUse = sourceProfile;
        if (documentId && store?.transformerConfig?.row_transformers?.[documentId]) {
          const savedData = store.transformerConfig.row_transformers[documentId];
          if (savedData?.demographic_override) {
            // Use row transformer values as defaults
            profileToUse = savedData.demographic_override;
          }
          
          // Initialize abstract profile data from saved transformer config if available
          if (savedData?.generated_profile) {
            setAbstractProfileData((prev) => ({
              ...prev,
              respondentCode: savedData.generated_profile.respondent_code || "",
              respondentBio: savedData.generated_profile.respondent_bio || "",
            }));
          }
        }
        
        setFormData({
          gender_gender_select: profileToUse.gender_gender_select || "",
          age_range_age_select: profileToUse.age_range_age_select || "",
          professional_background_background_select:
            profileToUse.professional_background_background_select || "",
          professional_experience_experience_select:
            profileToUse.professional_experience_experience_select || "",
          metro_city_city_select: profileToUse.metro_city_city_select || "",
          political_preference_preference_select:
            profileToUse.political_preference_preference_select || "",
        });
      }
    }, [document, documentId, rootStore]);

    const handleChange = (field: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleAbstractProfileChange = (field: string, value: string) => {
      setAbstractProfileData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleGenerateProfile = async () => {
      if (!documentId) return;

      setGenerating(true);
      try {
        const response = await rootStore.api.post(
          "/geopol/generate_abstract_profile",
          {
            clientid: documentId,
            demographic_profile: formData,
            additional_context: abstractProfileData.additionalContext,
          }
        );

        if (response.data) {
          setAbstractProfileData((prev) => ({
            ...prev,
            respondentCode: response.data.respondent_code || "",
            respondentBio: response.data.respondent_bio || "",
          }));
        }
      } catch (error) {
        console.error("Error generating abstract profile:", error);
        toast.error("Failed to generate abstract profile. Please try again.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setGenerating(false);
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const generatedProfile = {
        respondent_code: abstractProfileData.respondentCode,
        respondent_bio: abstractProfileData.respondentBio,
      };
      onSave(formData, generatedProfile);
    };

    if (!document) {
      return null;
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
          <h3 className="text-base font-semibold text-gray-800">
            Profile Configuration
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Demographic Profile Override Accordion */}
          <Disclosure as="div" defaultOpen>
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="text-left w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
                      <span className="block text-sm font-semibold text-gray-800">
                        Demographic Profile
                      </span>
                    </div>
                    <ChevronDownIcon
                      className={classNames(
                        open ? "-rotate-180" : "rotate-0",
                        "h-6 w-6 text-indigo-600 transform transition-transform duration-300"
                      )}
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="mt-4 space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Gender
                    </label>
                    <select
                      value={formData.gender_gender_select}
                      onChange={(e) =>
                        handleChange("gender_gender_select", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select Gender</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Age Range
                    </label>
                    <select
                      value={formData.age_range_age_select}
                      onChange={(e) =>
                        handleChange("age_range_age_select", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select Age Range</option>
                      {AGE_RANGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Professional Background
                    </label>
                    <select
                      value={formData.professional_background_background_select}
                      onChange={(e) =>
                        handleChange(
                          "professional_background_background_select",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select Profession</option>
                      {PROFESSION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Professional Experience
                    </label>
                    <select
                      value={formData.professional_experience_experience_select}
                      onChange={(e) =>
                        handleChange(
                          "professional_experience_experience_select",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select Experience</option>
                      {EXPERIENCE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Metro City
                    </label>
                    <select
                      value={formData.metro_city_city_select}
                      onChange={(e) =>
                        handleChange("metro_city_city_select", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select City</option>
                      {CITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Political Preference
                    </label>
                    <select
                      value={formData.political_preference_preference_select}
                      onChange={(e) =>
                        handleChange(
                          "political_preference_preference_select",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400"
                    >
                      <option value="">Select Leaning</option>
                      {LEANING_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>

          {/* Abstract Profile Generator Accordion */}
          <Disclosure as="div" defaultOpen>
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="text-left w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
                      <span className="block text-sm font-semibold text-gray-800">
                        Abstract Profile Generator
                      </span>
                    </div>
                    <ChevronDownIcon
                      className={classNames(
                        open ? "-rotate-180" : "rotate-0",
                        "h-4 w-4 text-gray-600 transform transition-transform duration-200"
                      )}
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="mt-3 space-y-3 p-3 bg-gray-50/50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Additional Context <span className="text-gray-400">(Optional)</span>
                    </label>
                    <textarea
                      value={abstractProfileData.additionalContext}
                      onChange={(e) =>
                        handleAbstractProfileChange(
                          "additionalContext",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400 resize-none"
                      rows={3}
                      placeholder="Add any additional context..."
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleGenerateProfile}
                    disabled={generating}
                    className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-sm hover:shadow transition-all duration-200"
                  >
                    {generating ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin">⏳</span>
                        Generating...
                      </span>
                    ) : (
                      "Generate Abstract Profile"
                    )}
                  </button>

                  {generating && (
                    <div className="flex justify-center">
                      <LoadingState />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Respondent Code
                    </label>
                    <input
                      type="text"
                      value={abstractProfileData.respondentCode}
                      onChange={(e) =>
                        handleAbstractProfileChange("respondentCode", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400 text-gray-700 font-mono"
                      placeholder="Generated code will appear here"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Respondent Bio
                    </label>
                    <textarea
                      value={abstractProfileData.respondentBio}
                      onChange={(e) =>
                        handleAbstractProfileChange("respondentBio", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white hover:border-gray-400 text-gray-700 resize-none"
                      rows={5}
                      placeholder="Generated anonymized bio will appear here"
                    />
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>

          {/* Profile Comparison View */}
          <Disclosure as="div" defaultOpen={false}>
            {({ open }) => {
              const store = rootStore?.geopolTransformerStore;
              const initialProfile = document?.demographic_profile_of_respondents || {};
              const transformedProfile = documentId && store?.transformerConfig?.row_transformers?.[documentId]?.demographic_override;
              const hasTransformedProfile = !!transformedProfile;

              const renderProfileField = (label: string, value: string) => (
                <div className="mb-2">
                  <span className="text-xs font-medium text-gray-600">{label}:</span>
                  <span className="ml-2 text-xs text-gray-800">
                    {value || <span className="text-gray-400 italic">N/A</span>}
                  </span>
                </div>
              );

              return (
                <>
                  <dt>
                    <Disclosure.Button className="text-left w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="h-1 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
                        <span className="block text-sm font-semibold text-gray-800">
                          Profile Comparison
                        </span>
                      </div>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "-rotate-180" : "rotate-0",
                          "h-4 w-4 text-gray-600 transform transition-transform duration-200"
                        )}
                        aria-hidden="true"
                      />
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Initial Profile */}
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-1 w-1.5 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                          <h4 className="text-xs font-semibold text-gray-800">Initial Profile</h4>
                          <span className="text-xs text-gray-500">(Source Index Document)</span>
                        </div>
                        <div className="space-y-1">
                          {renderProfileField("Gender", initialProfile.gender_gender_select)}
                          {renderProfileField("Age Range", initialProfile.age_range_age_select)}
                          {renderProfileField("Professional Background", initialProfile.professional_background_background_select)}
                          {renderProfileField("Professional Experience", initialProfile.professional_experience_experience_select)}
                          {renderProfileField("Metro City", initialProfile.metro_city_city_select)}
                          {renderProfileField("Political Preference", initialProfile.political_preference_preference_select)}
                        </div>
                      </div>

                      {/* Transformed Profile */}
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-1 w-1.5 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></div>
                          <h4 className="text-xs font-semibold text-gray-800">Transformed Profile</h4>
                          <span className="text-xs text-gray-500">(Row Transformer)</span>
                        </div>
                        {hasTransformedProfile ? (
                          <div className="space-y-1">
                            {renderProfileField("Gender", transformedProfile.gender_gender_select)}
                            {renderProfileField("Age Range", transformedProfile.age_range_age_select)}
                            {renderProfileField("Professional Background", transformedProfile.professional_background_background_select)}
                            {renderProfileField("Professional Experience", transformedProfile.professional_experience_experience_select)}
                            {renderProfileField("Metro City", transformedProfile.metro_city_city_select)}
                            {renderProfileField("Political Preference", transformedProfile.political_preference_preference_select)}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic py-2">
                            No transformed profile available
                          </div>
                        )}
                      </div>
                    </div>
                  </Disclosure.Panel>
                </>
              );
            }}
          </Disclosure>

          <div className="flex gap-2.5 pt-4 border-t border-gray-200">
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 font-medium text-sm shadow-sm hover:shadow transition-all duration-200"
            >
              Save Changes
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 font-medium text-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }
);
